<template>
  <my-giving-layout>
    <BfFastSubmitted
      v-if="!isLoading && isBfFastSubmitted"
      :amount="egiroTransactionStatus.paymentDetails.amount"
      :month="transactionDetails.month"
      :back="fromNotifications ? handleBack : null"
      @done="handleNext"
    />
    <div v-else class="w-full min-h-full bg-paper flex flex-col">
      <BackButton
        v-if="fromNotifications"
        class="w-full px-[20px] mb-[16px]"
        :customBack="handleBack"
      />
      <!-- Loading State -->
      <Loading v-if="isLoading" />

      <!-- Status Content -->
      <div
        v-else
        class="flex-1 flex flex-col items-center px-[24px] w-full md:max-w-2xl md:mx-auto"
        :class="isIos() ? 'pt-[134px]' : 'pt-[112px]'"
      >
        <!-- Static image for all status states -->
        <img :src="statusImage" alt="Status Image" :class="statusImageSize + ' mb-[32px]'" />
        <div v-if="getStatusString() === 'PENDING'">
          <div v-if="recurringGivingSelect === 'One-Time'">
            <div
              class="text-[1.5rem] md:text-[2.25rem] lg:text-[2.5rem] leading-[2rem] md:leading-[2.75rem] lg:leading-[3rem] font-bold text-[#21201E] text-center mb-[8px]"
            >
              Transfer in progress
            </div>
            <div
              class="text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem] text-[#2B2926] text-center mb-[24px]"
            >
              You can close the page - we'll notify you once it's completed.
            </div>
          </div>
        </div>

        <!-- SUCCESS Status -->
        <div v-if="getStatusString() === 'SUCCESS'">
          <div v-if="recurringGivingSelect === 'One-Time'">
            <div
              class="text-[1.5rem] md:text-[2.25rem] lg:text-[2.5rem] leading-[2rem] md:leading-[2.75rem] lg:leading-[3rem] font-bold text-[#21201E] tracking-[-0.03em] text-center mb-[8px]"
            >
              Successful
            </div>
            <div
              class="text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem] text-[#2B2926] text-center mb-[24px]"
            >
              {{ CurrentTime }}
            </div>
          </div>
          <div v-else>
            <div v-if="editMode">
              <div
                class="text-[2.25rem] md:text-[3.25rem] lg:text-[3.5rem] leading-[2.5rem] md:leading-[3.75rem] lg:leading-[4rem] font-black text-[#21201E] tracking-[-0.03em] text-center mb-[24px]"
              >
                Request is being processed
              </div>
              <div
                class="text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] leading-[1.5rem] md:leading-[2.375rem] lg:leading-[2.5rem] text-[#2B2926] mt-[24px] px-[16px]"
              >
                <p v-if="updateAction">
                  Request to update recurring giving is successfully submitted.
                </p>
                <p v-else>Request to remove recurring giving is successfully submitted.</p>
                <p class="mt-[16px]">
                  Check <b>Manage My Giving</b> in 1 working day for the status.
                </p>
              </div>
            </div>
            <div v-else>
              <div
                class="text-[2.25rem] md:text-[3.25rem] lg:text-[3.5rem] leading-[2.5rem] md:leading-[3.75rem] lg:leading-[4rem] font-black text-[#21201E] tracking-[-0.03em] text-center mb-[24px]"
              >
                Your application's on the way
              </div>
              <div
                class="text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] leading-[1.5rem] md:leading-[2.375rem] lg:leading-[2.5rem] text-[#2B2926] text-center mb-[24px]"
              >
                Your bank is processing it now. <br />Check <b>Manage My Giving</b> in 1 working day
                for the status.
              </div>
            </div>
          </div>
        </div>

        <!-- FAILED Status - One-Time Giving -->
        <div v-if="getStatusString() === 'FAILED' && recurringGivingSelect === 'One-Time'">
          <!-- Title -->
          <div
            class="text-[1.5rem] md:text-[2.25rem] lg:text-[2.5rem] leading-[2rem] md:leading-[2.75rem] lg:leading-[3rem] font-bold text-[#21201E] tracking-[-0.03em] text-center w-full mb-[8px]"
          >
            {{ errorTitle || 'Oops, something went wrong 😅' }}
          </div>

          <!-- Subtitle -->
          <div
            class="text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem] text-[#2B2926] text-center w-full mb-[24px]"
          >
            {{ errorSubtitle || 'Please try again in a moment!' }}
          </div>

          <!-- Spacer -->
          <div class="h-[20px]"></div>

          <!-- Info Section - Matching SUCCESS state styling -->
          <div class="text-center w-full">
            <div
              class="text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem] text-[#2B2926] mb-[4px]"
            >
              {{ CurrentTime }}
            </div>
            <div
              v-if="errorCode"
              class="text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem] text-[#2B2926] mb-[4px]"
            >
              Error Code: {{ errorCode }}
            </div>
            <div
              class="text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem] text-[#2B2926]"
            >
              Transaction ID: {{ transactionId || 'N/A' }}
            </div>
          </div>
        </div>

        <!-- FAILED Status - Recurring Giving -->
        <div v-if="getStatusString() === 'FAILED' && recurringGivingSelect !== 'One-Time'">
          <div
            class="text-[2.25rem] md:text-[3.25rem] lg:text-[3.5rem] leading-[2.5rem] md:leading-[3.75rem] lg:leading-[4rem] font-black text-[#21201E] tracking-[-0.03em] text-center mb-[24px]"
          >
            Setup failed
          </div>
          <div
            class="text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] leading-[1.5rem] md:leading-[2.375rem] lg:leading-[2.5rem] text-[#2B2926] mt-[24px] px-[16px] text-center"
          >
            We were not able to setup recurring giving for the selected bank account.
            <br /><br />Try again now, or go to MyGiving and set it up later.
          </div>
        </div>

        <!-- Transaction Details Grid (for One-Time giving) -->
        <div
          v-if="recurringGivingSelect === 'One-Time' && getStatusString() !== 'FAILED'"
          class="w-full flex flex-col gap-[8px]"
          :class="{
            'mb-[60px]': getStatusString() === 'SUCCESS' || getStatusString() === 'PENDING',
          }"
        >
          <div class="flex justify-between items-center w-full">
            <span
              class="text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] text-[#2B2926] w-[140px] md:w-auto"
              >Amount</span
            >
            <span
              class="text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] font-bold text-[#21201E] text-right tracking-[-0.48px]"
            >
              {{ transactionDetails.amount }}
            </span>
          </div>
          <div class="flex justify-between items-center w-full">
            <span
              class="text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] text-[#2B2926] w-[140px] md:w-auto"
              >Frequency</span
            >
            <span
              class="text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] font-bold text-[#21201E] text-right tracking-[-0.48px]"
            >
              {{ transactionDetails.frequency }}
            </span>
          </div>
          <!-- Conditional Month field - only show for Tithe, Building Fund, Overseas Missions -->
          <div v-if="shouldShowMonth" class="flex justify-between items-center w-full">
            <span
              class="text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] text-[#2B2926] w-[140px] md:w-auto"
              >Month</span
            >
            <span
              class="text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] font-bold text-[#21201E] text-right tracking-[-0.48px]"
            >
              {{ transactionDetails.month }}
            </span>
          </div>
          <div class="flex justify-between items-center w-full">
            <span
              class="text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] text-[#2B2926] w-[140px] md:w-auto"
              >Giving to</span
            >
            <span
              class="text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] font-bold text-[#21201E] text-right tracking-[-0.48px]"
            >
              {{ transactionDetails.givingto }}
            </span>
          </div>
          <div class="flex justify-between items-center w-full">
            <span
              class="text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] text-[#2B2926] w-[140px] md:w-auto"
              >Giving method</span
            >
            <span
              class="text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] font-bold text-[#21201E] text-right tracking-[-0.48px]"
            >
              {{ transactionDetails.givingmethod }}
            </span>
          </div>
        </div>
      </div>

      <!-- At the bottom of this full-height column, below the content if it grows. -->
      <div
        v-if="!isLoading && !fromNotifications"
        class="mt-auto w-full px-[20px] md:px-[24px] lg:px-[28px] pt-[32px] pb-[max(44px,calc(env(safe-area-inset-bottom)+16px))]"
      >
        <div class="w-full max-w-[600px] mx-auto flex flex-col gap-[8px] md:gap-[12px]">
          <!-- Secondary button for certain error actions (only for recurring giving) -->
          <div
            v-if="showSecondaryButton && recurringGivingSelect !== 'One-Time'"
            @click="handleSecondaryAction"
            class="border-2 border-[#42362B] w-full bg-[#F6F4F0] text-[#21201E] text-[1rem] md:text-[1.25rem] lg:text-[1.375rem] font-bold rounded-[100px] py-[16px] text-center"
          >
            {{ secondaryButtonText }}
          </div>

          <!-- Primary Back to MyGiving button (only for recurring giving) -->
          <div
            v-if="recurringGivingSelect !== 'One-Time'"
            @click="handleNext"
            class="bg-[#42362B] w-full text-white text-[1rem] md:text-[1.25rem] lg:text-[1.375rem] font-bold rounded-[100px] py-[16px] text-center"
            :class="{ 'mb-[0]': editMode }"
          >
            Back to MyGiving
          </div>

          <!-- Edit mode "See status" button -->
          <div
            v-if="editMode"
            @click="goToCheckStatus()"
            class="border-[2px] border-[#42362B] w-full bg-[#F6F4F0] text-[#21201E] text-[1rem] md:text-[1.25rem] lg:text-[1.375rem] font-semibold rounded-[100px] py-[14px] md:py-[18px] lg:py-[20px] text-center"
          >
            See status
          </div>

          <!-- FAILED State Buttons for One-Time giving -->
          <template
            v-if="
              getStatusString() === 'FAILED' && recurringGivingSelect === 'One-Time' && !editMode
            "
          >
            <!-- Contact Support: 2 buttons -->
            <template v-if="errorAction === 'CONTACT_SUPPORT' || errorAction === 'CHECK_BANK'">
              <div class="w-full border-2 border-[#42362b] rounded-[100px]">
                <button
                  class="w-full py-[16px] md:py-[20px] lg:py-[22px] px-[24px] font-bold text-[1rem] md:text-[1.25rem] lg:text-[1.375rem] text-[#42362b] leading-[1.5rem] md:leading-[2rem] flex items-center justify-center bg-transparent"
                  @click="openCrispChat"
                >
                  Contact Support
                </button>
              </div>
              <button
                class="w-full bg-[#42362b] rounded-[100px] py-[16px] md:py-[20px] lg:py-[22px] px-[24px] font-bold text-[1rem] md:text-[1.25rem] lg:text-[1.375rem] text-white leading-[1.5rem] md:leading-[2rem] flex items-center justify-center"
                @click="handleNext"
              >
                Back to MyGiving
              </button>
            </template>

            <!-- Try Again: 2 buttons -->
            <template v-else-if="errorAction === 'TRY_AGAIN'">
              <button
                class="w-full border-2 border-[#42362b] rounded-[100px] py-[16px] md:py-[20px] lg:py-[22px] px-[24px] font-bold text-[1rem] md:text-[1.25rem] lg:text-[1.375rem] text-[#42362b] leading-[1.5rem] md:leading-[2rem] flex items-center justify-center"
                @click="handleRetry"
              >
                Try Again
              </button>
              <button
                class="w-full bg-[#42362b] rounded-[100px] py-[16px] md:py-[20px] lg:py-[22px] px-[24px] font-bold text-[1rem] md:text-[1.25rem] lg:text-[1.375rem] text-white leading-[1.5rem] md:leading-[2rem] flex items-center justify-center"
                @click="handleNext"
              >
                Back to MyGiving
              </button>
            </template>

            <!-- All other FAILED states: 1 button -->
            <button
              v-else
              class="w-full bg-[#42362b] rounded-[100px] py-[16px] md:py-[20px] lg:py-[22px] px-[24px] font-bold text-[1rem] md:text-[1.25rem] lg:text-[1.375rem] text-white leading-[1.5rem] md:leading-[2rem] flex items-center justify-center"
              @click="handleNext"
            >
              Back to MyGiving
            </button>
          </template>

          <!-- Default Back to MyGiving button for non-FAILED or edit mode -->
          <BottomButton
            v-if="
              editMode ||
              (getStatusString() !== 'FAILED' && recurringGivingSelect === 'One-Time') ||
              (getStatusString() === 'FAILED' && recurringGivingSelect !== 'One-Time')
            "
            id="generate-qr-code"
            text="Back to MyGiving"
            @click="handleNext"
            class="w-full mb-[0]"
          />
        </div>
      </div>
    </div>
    <!-- A Building Fund gift sent from Give now: the building video over the BF2026
         screen, in place of the hands animation other gifts get. Its Continue goes to
         My Giving, as the hands animation's Back to MyGiving does: the screen under it
         is the receipt for opening the gift later, from Updates or a push. -->
    <PledgeSubmittedVideo
      v-if="playsBfVideo"
      :amount="egiroTransactionStatus.paymentDetails?.amount"
      :transaction-id="transactionId"
      :fade-out="false"
      @continue="leaveBfVideo"
    />
  </my-giving-layout>
</template>

<script>
import { mapState } from 'vuex';
import lottie from 'lottie-web';
import MyGivingLayout from '@/components/MyGivingLayout.vue';
import { BANK_ENUM } from '@/constants/MyGiving/bank-list';
import { ERROR_STATE_MAP } from '@/constants/MyGiving/ERROR_STATES';
import BottomButton from '@/components/common/BottomButton.vue';
import BackButton from '@/components/common/BackButton.vue';
import Loading from '@/components/common/LoadingState.vue';
import { isIosNativeOnly } from '@/utils/platform-check';
import navigationService from '@/utils/navigation-service';
import { openCrispChat } from '@/utils/crisp';
import { useIonRouter } from '@ionic/vue';
import BfFastSubmitted from '@/components/MyGiving/BuildingFundPledge/BfFastSubmitted.vue';
import PledgeSubmittedVideo from '@/components/MyGiving/BuildingFundPledge/PledgeSubmittedVideo.vue';
import { isBfFastGift, playsBfGiftVideo } from '@/utils/building-fund-pledge';

export default {
  name: 'TransactionStatus',
  setup() {
    const ionRouter = useIonRouter();
    return { ionRouter };
  },
  components: {
    MyGivingLayout,
    BottomButton,
    BackButton,
    Loading,
    BfFastSubmitted,
    PledgeSubmittedVideo,
  },
  data() {
    return {
      isLoading: true,
      // The building video has been continued past (see playsBfVideo).
      videoDone: false,
      clockAnimationInstance: null,
      egiroTransactionStatus: {
        status: 'PENDING',
        message: '',
        paymentDetails: null,
      },
      transactionDetails: {
        givingto: '',
        givingmethod: '',
        amount: '',
        frequency: '',
        month: '',
        transactionMessage: '',
        transactionTime: null,
        bankAccountNo: '',
      },
      recurringGivingSelect: 'One-Time',
      editMode: false,
      updateAction: false,
      ACTION_MAP: {
        CONTACT_SUPPORT: {
          title: 'Your transaction could not be completed 🥲',
          subtitle: 'Please contact support for help!',
          showSecondary: true,
          secondaryText: 'Contact Support',
        },
        TRY_AGAIN: {
          title: 'That took longer than expected.',
          subtitle: "Let's try that again!",
          showSecondary: true,
          secondaryText: 'Try again',
        },
        TRY_AGAIN_LATER: {
          title: 'Oops, something went wrong 😅',
          subtitle: 'Please try again in a moment!',
          showSecondary: false,
        },
        TRY_AGAIN_TOMORROW: {
          title: "You've reached today's transaction limit",
          subtitle: 'Please try again tomorrow!',
          showSecondary: false,
        },
        UNLINK_AND_LINK_ACCOUNT: {
          title: 'Your transaction could not be completed 🥲',
          subtitle: 'Please unlink your bank account and link again.',
        },
        // The bank never confirmed the transfer, so it may still have gone
        // through: check before giving again rather than retry blindly.
        CHECK_BANK: {
          title: "We couldn't confirm this transaction",
          subtitle: 'Please check your bank account before giving again.',
          showSecondary: true,
          secondaryText: 'Contact Support',
        },
      },
      ERROR_ACTION_IMAGE_MAP: {
        CONTACT_SUPPORT: 'transaction-not-completed.svg',
        TRY_AGAIN: 'longer-than-expected.svg',
        TRY_AGAIN_LATER: 'something-went-wrong.svg',
        TRY_AGAIN_TOMORROW: 'reached-limit.svg',
        UNLINK_AND_LINK_ACCOUNT: 'transaction-not-completed.svg',
        CHECK_BANK: 'transaction-not-completed.svg',
      },
    };
  },
  computed: {
    ...mapState('myGiving', ['selectedGiveType', 'selectedGiveMethod']),
    // A Building Fund FAST gift that went through gets the BF2026 screen. Decided
    // from the status response, not the store, so it also holds when the page is
    // opened from a push or Updates, or a pending gift succeeds while polling.
    // Give now asked for the building video (bfFastSubmittedPath). Shown from the
    // first frame, over the loading state too: Give now only asks once the gift
    // has gone through. Opened from Updates or a push, there is no video. Every
    // Building Fund gift gets it, no flag; reduced motion is checked here as well.
    playsBfVideo() {
      return this.$route?.query?.celebrate === '1' && !this.videoDone && playsBfGiftVideo();
    },
    isBfFastSubmitted() {
      return (
        this.getStatusString() === 'SUCCESS' &&
        this.recurringGivingSelect === 'One-Time' &&
        isBfFastGift(this.transactionDetails.givingto)
      );
    },
    statusImage() {
      if (this.getStatusString() === 'FAILED') {
        // Check if errorAction exists and map to corresponding SVG
        if (this.errorAction && this.ERROR_ACTION_IMAGE_MAP[this.errorAction]) {
          const svgFile = this.ERROR_ACTION_IMAGE_MAP[this.errorAction];
          return `/assets/icons/my-giving/${svgFile}`;
        }
        // Fallback to default failure image
        return '/assets/images/my-giving/rcfail.png';
      }

      if (this.getStatusString() === 'SUCCESS') {
        const imageFile =
          this.recurringGivingSelect === 'One-Time' ? 'transaction-success.png' : 'rcsuccess.png';
        return `/assets/images/my-giving/${imageFile}`;
      }

      // PENDING
      return '/assets/images/my-giving/pending_state.webp';
    },
    statusImageSize() {
      if (this.getStatusString() === 'FAILED') {
        const wideStates = ['CONTACT_SUPPORT', 'UNLINK_AND_LINK_ACCOUNT', 'CHECK_BANK'];
        return wideStates.includes(this.errorAction)
          ? 'w-[211px] h-[144px]'
          : 'w-[200px] h-[144px]';
      }
      return 'w-[144px] h-[144px]';
    },
    CurrentTime() {
      const date = new Date(this.transactionDetails.transactionTime);

      const datePart = date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });

      const timePart = date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });

      return `${datePart}, ${timePart}`;
    },
    errorCode() {
      // The status API returns errorCode as its own field; use it. This used to
      // be parsed out of the message built by formatErrorMessage
      // ("Error: 1172 - ..."), looking for "errorCode:", which that message
      // never contains — so no failure ever got its tailored screen.
      const fromApi = this.egiroTransactionStatus?.paymentDetails?.errorCode;
      if (fromApi !== undefined && fromApi !== null && String(fromApi).trim() !== '') {
        return String(fromApi).trim();
      }
      const message = this.transactionDetails?.transactionMessage;
      if (!message) {
        return null;
      }
      const match =
        message.match(/errorCode:\s*([A-Z0-9_]+)/i) || message.match(/^Error:\s*([A-Z0-9_]+)\s+-/i);
      return match ? match[1] : null;
    },
    errorAction() {
      if (!this.errorCode) {
        return null;
      }
      // Try string key first
      let action = ERROR_STATE_MAP[this.errorCode];
      // If not found and errorCode is numeric, try as number
      if (!action && /^\d+$/.test(this.errorCode)) {
        action = ERROR_STATE_MAP[Number(this.errorCode)];
      }
      return action || null;
    },
    errorTitle() {
      if (!this.errorAction || !this.ACTION_MAP[this.errorAction]) {
        return null;
      }
      return this.ACTION_MAP[this.errorAction].title;
    },
    errorSubtitle() {
      if (!this.errorAction || !this.ACTION_MAP[this.errorAction]) {
        return null;
      }
      return this.ACTION_MAP[this.errorAction].subtitle;
    },
    transactionId() {
      // Try to get from route params first
      if (this.$route?.params?.transactionId) {
        return this.$route.params.transactionId;
      }
      // Try to get from transactionDetails
      if (this.transactionDetails?.transactionId) {
        return this.transactionDetails.transactionId;
      }
      if (this.transactionDetails?.id) {
        return this.transactionDetails.id;
      }
      return null;
    },
    fromNotifications() {
      const hasQueryParam = this.$route?.query?.from === 'notifications';
      const fromUpdatesPage = this.$router?.options?.history?.state?.back === '/updates';
      return hasQueryParam || fromUpdatesPage;
    },
    shouldShowMonth() {
      const givingType = this.transactionDetails?.givingto || '';
      // Hide month for Offering and GenerationS Fund, show for others
      return !['Offering', 'GenerationS Fund'].includes(givingType);
    },
    showSecondaryButton() {
      if (!this.errorAction || !this.ACTION_MAP[this.errorAction]) {
        return false;
      }
      return this.ACTION_MAP[this.errorAction].showSecondary || false;
    },
    secondaryButtonText() {
      if (!this.errorAction || !this.ACTION_MAP[this.errorAction]) {
        return '';
      }
      return this.ACTION_MAP[this.errorAction].secondaryText || '';
    },
  },
  ionViewWillEnter() {
    // Ionic keeps this page alive, so a new gift must not inherit the last video.
    this.videoDone = false;
    const backTarget = this.fromNotifications ? '/updates' : '/my-giving';
    navigationService.registerBackHandler(() => {
      this.ionRouter.navigate(backTarget, 'back', 'pop');
    }, this.$route.path);
  },
  async ionViewDidEnter() {
    const transactionId = this.$route.params.transactionId;
    if (!transactionId) {
      // Redirect to my-giving page if no transaction ID
      this.$router.replace('/my-giving');
      return;
    }

    await this.fetchTransactionStatus(transactionId);
  },
  ionViewWillLeave() {
    if (this.clockAnimationInstance) {
      this.clockAnimationInstance.destroy();
      this.clockAnimationInstance = null;
    }
    navigationService.clearBackHandler();
  },
  ionViewDidLeave() {
    this.isLoading = true;
  },
  beforeUnmount() {
    if (this.clockAnimationInstance) {
      this.clockAnimationInstance.destroy();
      this.clockAnimationInstance = null;
    }
  },
  methods: {
    isIos() {
      return isIosNativeOnly();
    },
    getStatusString() {
      return this.egiroTransactionStatus.status;
    },
    formatErrorMessage(statusData, defaultMessage) {
      if (statusData.errorCode && statusData.errorDescription) {
        return `Error: ${statusData.errorCode} - ${statusData.errorDescription}`;
      } else if (statusData.errorCode) {
        return statusData.errorCode;
      } else if (statusData.errorDescription) {
        return statusData.errorDescription;
      }
      return defaultMessage;
    },

    /**
     * Centralized method to handle status response and update UI
     * @param {Object} statusData - Transaction status data from API
     * @param {boolean} showPendingUI - Whether to show PENDING UI (default: true)
     * @returns {string} - The status string ('SUCCESS', 'FAILED', 'PENDING', or null)
     */
    handleStatusResponse(statusData, showPendingUI = true) {
      if (!statusData || !statusData.status) {
        return null;
      }

      const status = String(statusData.status).toUpperCase().trim();
      this.isLoading = false;

      if (status === 'SUCCESS') {
        this.egiroTransactionStatus = {
          status: 'SUCCESS',
          message: 'Transaction completed successfully',
          paymentDetails: statusData,
        };
        this.updateTransactionDetails(statusData);
        if (this.clockAnimationInstance) {
          this.clockAnimationInstance.destroy();
          this.clockAnimationInstance = null;
        }
        return 'SUCCESS';
      } else if (status === 'FAILED') {
        const errorMessage = this.formatErrorMessage(statusData, 'Transaction failed');
        this.egiroTransactionStatus = {
          status: 'FAILED',
          message: errorMessage,
          paymentDetails: statusData,
        };
        this.updateTransactionDetails(statusData);
        if (this.clockAnimationInstance) {
          this.clockAnimationInstance.destroy();
          this.clockAnimationInstance = null;
        }
        return 'FAILED';
      } else if (status === 'PENDING') {
        if (showPendingUI) {
          this.egiroTransactionStatus = {
            status: 'PENDING',
            message: '',
            paymentDetails: statusData,
          };
          this.updateTransactionDetails(statusData);
          this.$nextTick(() => {
            this.initClockAnimation();
          });
          return 'PENDING';
        } else {
          return 'PENDING';
        }
      }

      return null;
    },
    initClockAnimation() {
      if (this.clockAnimationInstance || !this.$refs.clockAnimation) {
        return;
      }

      this.clockAnimationInstance = lottie.loadAnimation({
        container: this.$refs.clockAnimation,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        path: '/clock-moving.json',
        rendererSettings: {
          preserveAspectRatio: 'xMidYMid meet',
        },
      });
    },

    async pollFastTransactionStatus(transactionId) {
      const maxRetries = 30;
      const pollInterval = 2000;
      let retries = 0;

      const poll = async () => {
        try {
          const response = await this.$store.dispatch(
            'myGiving/checkFastTransactionStatus',
            transactionId,
          );
          const statusData = response?.data || {};
          const status = String(statusData.status || '')
            .toUpperCase()
            .trim();

          if (status === 'SUCCESS' || status === 'FAILED') {
            this.handleStatusResponse(statusData, false);
            return;
          }

          if (status === 'PENDING') {
            const handledStatus = this.handleStatusResponse(statusData, true);
            if (handledStatus !== 'PENDING') {
              return;
            }

            retries++;
            if (retries >= maxRetries) {
              this.isLoading = false;
              return;
            }

            setTimeout(poll, pollInterval);
            return;
          }

          this.$router.replace('/my-giving');
        } catch (error) {
          console.error('[TransactionStatus] Poll error:', error);

          this.isLoading = false;
          if (this.egiroTransactionStatus.status !== 'PENDING') {
            this.egiroTransactionStatus = {
              status: 'PENDING',
              message: '',
              paymentDetails: null,
            };
          }

          retries++;
          if (retries >= maxRetries) {
            return;
          }
          setTimeout(poll, pollInterval);
        }
      };

      poll();
    },

    updateTransactionDetails(statusData) {
      const formatAmount = amount => {
        if (amount === null || amount === undefined) return '';
        return `$${Number(amount).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      };

      const getBankName = bankCode => {
        if (bankCode && BANK_ENUM[bankCode]) {
          return BANK_ENUM[bankCode].name;
        }
        return bankCode;
      };

      const bankName = getBankName(statusData.bankCode);
      const accountNo = statusData.bankAccountNo || '';
      const linkedAccount = accountNo ? `${bankName} ${accountNo}` : bankName;

      this.transactionDetails = {
        givingto: statusData.givingCategory,
        givingmethod: linkedAccount,
        amount: formatAmount(statusData.amount),
        frequency: statusData.frequency || '',
        month: statusData.monthOfGiving || '',
        transactionMessage: this.egiroTransactionStatus.message,
        transactionTime: statusData.createdAt,
        bankAccountNo: accountNo,
        transactionId: statusData.transactionId || statusData.id || null,
      };
    },

    async fetchTransactionStatus(transactionId) {
      try {
        const response = await this.$store.dispatch(
          'myGiving/checkFastTransactionStatus',
          transactionId,
        );
        const statusData = response?.data || {};
        const status = statusData.status?.toUpperCase();

        const handledStatus = this.handleStatusResponse(statusData, true);

        if (status === 'PENDING') {
          this.pollFastTransactionStatus(transactionId);
        } else if (!handledStatus) {
          this.$router.replace('/my-giving');
        }
      } catch (error) {
        console.error('[TransactionStatus] Error fetching status:', error);

        // Treat all errors as PENDING and start polling
        this.isLoading = false;
        this.egiroTransactionStatus = {
          status: 'PENDING',
          message: '',
          paymentDetails: null,
        };
        this.pollFastTransactionStatus(transactionId);
      }
    },

    handleClose() {
      this.$router.replace('/my-giving');
    },

    handleNext() {
      this.$router.replace({ path: '/my-giving' });
    },
    leaveBfVideo() {
      this.videoDone = true;
      this.handleNext();
    },
    openCrispChat() {
      openCrispChat({ showFirst: true });
    },
    handleRetry() {
      this.$router.replace({ path: '/my-giving' });
    },
    goToCheckStatus() {
      this.$router.replace({ path: '/my-giving/manage' });
    },
    handleSecondaryAction() {
      // Handle secondary button click based on error action
      if (this.errorAction === 'TRY_AGAIN') {
        // Retry the transaction - go back to giving page
        this.$router.replace({ path: '/my-giving' });
      } else if (this.errorAction === 'CONTACT_SUPPORT') {
        // Open email client to contact support
        window.location.href = 'mailto:accounts@heartofgodchurch.org';
      }
    },
    handleBack() {
      if (this.fromNotifications) {
        if (this.ionRouter?.navigate) {
          this.ionRouter.navigate('/updates', 'back', 'pop');
          return;
        }

        this.$router.replace('/updates');
        return;
      }

      if (window.history.length > 1) {
        this.$router.back();
        return;
      }
      this.$router.push('/updates');
    },
  },
};
</script>

<style scoped>
img {
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  user-drag: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  pointer-events: none;
}
</style>
